/**
 * 基于 Map 插入序的 LRU 容器：最久未 touch 的 key 位于最前，超出容量时优先淘汰。
 *
 * 适用于「条目进入 idle 后暂留、重新激活时摘除、超出上限淘汰最老条目」等场景，
 * 与具体业务数据结构解耦。
 *
 * @module utils/lruCache
 */

/**
 * 规范化 key，空值返回空串。
 * @param {unknown} key
 * @returns {string}
 */
function normalizeKey(key) {
  return String(key ?? '').trim();
}

/**
 * LRU 缓存容器。
 *
 * @example
 * const lru = new LruCache({ maxSize: 200 });
 * lru.touch('a');           // 标记 a 为最近使用
 * lru.delete('a');          // 重新激活时从 idle 池摘除
 * const evicted = lru.evictOverflow(); // ['oldestKey', ...]
 */
export class LruCache {
  /**
   * @param {object} [options]
   * @param {number} [options.maxSize=Infinity] 最大保留条目数；0 表示不保留任何条目
   */
  constructor(options = {}) {
    const maxSize = options.maxSize ?? Infinity;
    if (typeof maxSize !== 'number' || !Number.isFinite(maxSize) || maxSize < 0) {
      throw new Error('[LruCache] maxSize must be a non-negative finite number');
    }
    /** @type {number} */
    this.maxSize = maxSize;
    /** @type {Map<string, unknown>} */
    this._entries = new Map();
  }

  /** @returns {number} 当前条目数 */
  get size() {
    return this._entries.size;
  }

  /**
   * 读取条目值，不改变 LRU 顺序。
   * @param {unknown} key
   * @returns {unknown | undefined}
   */
  get(key) {
    const k = normalizeKey(key);
    if (!k) {
      return undefined;
    }
    return this._entries.get(k);
  }

  /**
   * 是否包含指定 key。
   * @param {unknown} key
   * @returns {boolean}
   */
  has(key) {
    const k = normalizeKey(key);
    if (!k) {
      return false;
    }
    return this._entries.has(k);
  }

  /**
   * 写入或更新条目，并标记为最近使用（移至末尾）。
   * @param {unknown} key
   * @param {unknown} [value] 关联值；省略时写入 `undefined`
   * @returns {void}
   */
  touch(key, value) {
    const k = normalizeKey(key);
    if (!k) {
      return;
    }
    // 先删再写，利用 Map 插入序把 key 移到末尾
    if (this._entries.has(k)) {
      this._entries.delete(k);
    }
    this._entries.set(k, value);
  }

  /**
   * 删除单个条目。
   * @param {unknown} key
   * @returns {boolean} 是否删除了已有条目
   */
  delete(key) {
    const k = normalizeKey(key);
    if (!k) {
      return false;
    }
    return this._entries.delete(k);
  }

  /**
   * 批量删除条目（例如条目重新进入活跃态时从 idle 池摘除）。
   * @param {Iterable<unknown>} keys
   * @returns {void}
   */
  deleteMany(keys) {
    if (!keys) {
      return;
    }
    for (const key of keys) {
      this.delete(key);
    }
  }

  /**
   * 淘汰超出 `maxSize` 的最老条目。
   * @returns {string[]} 被淘汰的 key 列表（从最老到较新）
   */
  evictOverflow() {
    const evicted = [];
    // 条目数超过上限时，反复淘汰 Map 首项（最久未 touch）
    while (this._entries.size > this.maxSize) {
      const oldestKey = this._entries.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }
      this._entries.delete(oldestKey);
      evicted.push(oldestKey);
    }
    return evicted;
  }

  /**
   * 清空全部条目。
   * @returns {void}
   */
  clear() {
    this._entries.clear();
  }
}
